import * as React from "react";
import {
  Box,
  Button,
  Field as ChakraField,
  HStack,
  Input,
  Textarea,
  Text,
  VStack,
  RadioGroup,
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import {
  Formik,
  Form,
  Field,
  FieldProps,
  FormikHelpers,
  FormikProps,
} from "formik";

import {
  raiseComplaintReset,
  fetchDispositionsRequest,
} from "store/bbps/Complaint/bbpsComplaintActions";
import { RootState } from "store/rootReducer";
import { BbpsComplainLayout } from "layouts/BbpsComplainLayout/BbpsComplainLayout";
import { UniversalSelect } from "layouts/UniversalSelect/UniversalSelect";
import { bbpsComplaintService } from "services/api.services/bbpsComplaint/bbpsComplaintService";

type UniversalSelectItem = { label: string; value: string };
type ComplaintMode = "orderId" | "txnRefId";

interface RaiseTxnComplaintFormValues {
  mode: ComplaintMode;
  orderId: string;
  txnRefId: string;
  disposition: string; // store CODE e.g. "D11"
  description: string;
}

const Row: React.FC<{ label: string; value?: string | null }> = ({
  label,
  value,
}) => {
  if (!value) return null;
  return (
    <HStack justify="space-between">
      <Text fontSize="sm" color="gray.600">
        {label}
      </Text>
      <Text
        fontSize="sm"
        fontWeight="semibold"
        color="gray.800"
        textAlign="right"
      >
        {value}
      </Text>
    </HStack>
  );
};

const FloatingInputField: React.FC<
  {
    label: string;
    maxLength?: number;
    required?: boolean;
  } & FieldProps
> = ({ label, field, form, maxLength, required }) => {
  const [focus, setFocus] = React.useState(false);
  const value = String(field.value ?? "");
  const active = focus || value.length > 0;

  const error =
    (form.touched as any)?.[field.name] && (form.errors as any)?.[field.name];

  return (
    <ChakraField.Root invalid={!!error}>
      <Box position="relative" width="100%">
        <Input
          {...field}
          value={value}
          maxLength={maxLength}
          onFocus={() => setFocus(true)}
          onBlur={(e) => {
            setFocus(false);
            field.onBlur(e);
          }}
          width="100%"
          h="52px"
          pt="18px"
          bg="white"
          borderColor="blackAlpha.200"
          _hover={{ borderColor: "blackAlpha.300" }}
          _focus={{ borderColor: "blue.400", boxShadow: "0 0 0 1px" }}
          required={required}
          autoComplete="new-password"
        />

        <Text
          position="absolute"
          left="12px"
          top={active ? "8px" : "50%"}
          transform={active ? "translateY(0)" : "translateY(-50%)"}
          fontSize={active ? "xs" : "sm"}
          color={active ? "gray.600" : "gray.500"}
          pointerEvents="none"
          transition="all 120ms ease"
          bg="white"
          px="6px"
        >
          {label}
        </Text>
      </Box>

      {error ? (
        <ChakraField.ErrorText>{String(error)}</ChakraField.ErrorText>
      ) : null}
    </ChakraField.Root>
  );
};

// Optimized Textarea Field using useRef to prevent re-renders
const FloatingTextareaField: React.FC<
  {
    label: string;
    maxLength?: number;
    required?: boolean;
  } & FieldProps
> = ({ label, field, form, maxLength, required }) => {
  const [focus, setFocus] = React.useState(false);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const [_charCount, setCharCount] = React.useState(0);
  
  const value = String(field.value ?? "");
  const active = focus || value.length > 0;

  const error =
    (form.touched as any)?.[field.name] && (form.errors as any)?.[field.name];

  // Update character count without causing Formik re-render
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setCharCount(newValue.length);
    field.onChange(e); // Update Formik value
  };

  // Initialize character count
  React.useEffect(() => {
    setCharCount(value.length);
  }, [value.length]);

  return (
    <ChakraField.Root invalid={!!error}>
      <Box position="relative" width="100%">
        <Textarea
          ref={textareaRef}
          {...field}
          value={value}
          onChange={handleChange}
          maxLength={maxLength}
          onFocus={() => setFocus(true)}
          onBlur={(e) => {
            setFocus(false);
            field.onBlur(e);
          }}
          width={["100%", "50%"]}
          minH="100px"
          pt="24px"
          pb="12px"
          bg="white"
          borderColor="blackAlpha.200"
          _hover={{ borderColor: "blackAlpha.300" }}
          _focus={{ borderColor: "blue.400", boxShadow: "0 0 0 1px" }}
          required={required}
          resize="vertical"
          rows={4}
        />

        <Text
          position="absolute"
          left="12px"
          top={active ? "8px" : "36px"}
          transform={active ? "translateY(0)" : "translateY(-50%)"}
          fontSize={active ? "xs" : "sm"}
          color={active ? "gray.600" : "gray.500"}
          pointerEvents="none"
          transition="all 120ms ease"
          bg="white"
          px="6px"
          zIndex={1}
        >
          {label}
        </Text>
      </Box>

      {error ? (
        <ChakraField.ErrorText>{String(error)}</ChakraField.ErrorText>
      ) : null}
    </ChakraField.Root>
  );
};

export const RaiseTxnComplaintPage: React.FC = () => {
  const dispatch = useDispatch();
  const _raise = useSelector((s: RootState) => s.bbpsComplaint.raise);

  const dispositionsBox = useSelector(
    (s: RootState) => s.bbpsComplaint.dispositions,
  );

  const [submitting, setSubmitting] = React.useState(false);
  const [result, setResult] = React.useState<any>(null);
  const [error, setError] = React.useState<string>("");

  // Fetch dispositions only once on mount (if not already loaded)
  React.useEffect(() => {
    // Only fetch if we don't have disposition data yet
    if (!dispositionsBox.data || dispositionsBox.data.length === 0) {
      dispatch(fetchDispositionsRequest());
    }
  }, []); // Empty deps - only run once

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      dispatch(raiseComplaintReset());
    };
  }, []);

  const dispositionItems: UniversalSelectItem[] = React.useMemo(() => {
    const list = dispositionsBox.data ?? [];
    return list.map((d) => ({ label: d.label, value: d.code }));
  }, [dispositionsBox.data]);

  // Memoize initial values to prevent re-renders
  const initialValues: RaiseTxnComplaintFormValues = React.useMemo(
    () => ({
      mode: "orderId",
      orderId: "",
      txnRefId: "",
      disposition: "",
      description: "",
    }),
    [],
  );

  const validate = (values: RaiseTxnComplaintFormValues) => {
    const e: Partial<Record<keyof RaiseTxnComplaintFormValues, string>> = {};

    const mode = values.mode;
    const orderId = values.orderId?.trim() ?? "";
    const txnRefId = values.txnRefId?.trim() ?? "";
    const disposition = values.disposition?.trim() ?? "";
    const description = values.description?.trim() ?? "";

    // Validate based on mode
    if (mode === "orderId") {
      if (!orderId) e.orderId = "Order ID is required.";
      if (orderId.length > 20)
        e.orderId = "Order ID must be <= 20 characters.";
    } else {
      if (!txnRefId) e.txnRefId = "Transaction Reference ID is required.";
      if (txnRefId.length > 50)
        e.txnRefId = "Transaction Reference ID must be <= 50 characters.";
    }

    if (!disposition) e.disposition = "Please select a reason.";

    if (!description) e.description = "Description is required.";
    if (description.length > 300)
      e.description = "Description must be <= 300 characters.";

    return e;
  };

  const handleSubmit = async (
    values: RaiseTxnComplaintFormValues,
    helpers: FormikHelpers<RaiseTxnComplaintFormValues>,
  ) => {
    if (submitting) return;

    setSubmitting(true);
    setResult(null);
    setError("");

    try {
      // Use single unified API - send both fields, backend will handle which one to use
      const payload: any = {
        disposition: values.disposition.trim(),
        description: values.description.trim(),
      };

      // Add orderId or txnReferenceId based on mode
      if (values.mode === "orderId") {
        payload.orderId = values.orderId.trim();
      } else {
        payload.txnReferenceId = values.txnRefId.trim();
      }

      const res = await bbpsComplaintService.raiseComplaint(payload);

      // Check if API call was successful
      if (res?.data?.status && res?.data?.data) {
        setResult(res.data.data);
        helpers.resetForm();
      } else {
        // Handle API error response
        setError(res?.data?.message || "Failed to raise complaint");
      }
    } catch (err: any) {
      setError(err?.message || "Something went wrong");
    } finally {
      setSubmitting(false);
      helpers.setSubmitting(false);
    }
  };

  return (
    <BbpsComplainLayout title="Raise a Transaction Complaint" backTo="/bbps">
      <Formik<RaiseTxnComplaintFormValues>
        initialValues={initialValues}
        validate={validate}
        validateOnBlur
        validateOnChange
        onSubmit={handleSubmit}
      >
        {(fp: FormikProps<RaiseTxnComplaintFormValues>) => {
          const canSubmit =
            fp.isValid &&
            !submitting &&
            !fp.isSubmitting &&
            !!fp.values.disposition.trim() &&
            !!fp.values.description.trim() &&
            (fp.values.mode === "orderId"
              ? !!fp.values.orderId.trim()
              : !!fp.values.txnRefId.trim());

          return (
            <Form>
              <VStack align="stretch" gap={4}>
                {/* Mode Selection */}
                <Box>
                  <Text fontSize="sm" fontWeight="semibold" mb={2}>
                    Choose Complaint Type
                  </Text>
                  <RadioGroup.Root
                    value={fp.values.mode}
                    onValueChange={(details) => {
                      const newMode = details.value as ComplaintMode;
                      fp.setFieldValue("mode", newMode);
                      
                      // Clear ALL fields when mode changes
                      fp.setFieldValue("orderId", "");
                      fp.setFieldValue("txnRefId", "");
                      fp.setFieldValue("disposition", ""); // ✅ Reset disposition
                      fp.setFieldValue("description", ""); // ✅ Reset description
                      
                      // Clear result and error messages
                      setResult(null);
                      setError("");
                      
                      // Reset touched state for clean UX
                      fp.setTouched({});
                    }}
                  >
                    <HStack gap={6}>
                      <RadioGroup.Item value="orderId">
                        <RadioGroup.ItemHiddenInput />
                        <RadioGroup.ItemIndicator />
                        <RadioGroup.ItemText>
                          By Order ID
                        </RadioGroup.ItemText>
                      </RadioGroup.Item>
                      <RadioGroup.Item value="txnRefId">
                        <RadioGroup.ItemHiddenInput />
                        <RadioGroup.ItemIndicator />
                        <RadioGroup.ItemText>
                          By Transaction Ref ID
                        </RadioGroup.ItemText>
                      </RadioGroup.Item>
                    </HStack>
                  </RadioGroup.Root>
                </Box>

                {/* Order ID or TxnRefId based on mode */}
                {fp.values.mode === "orderId" ? (
                  <Field name="orderId">
                    {(p: FieldProps) => (
                      <FloatingInputField
                        {...p}
                        label="Order ID"
                        maxLength={30}
                        required
                      />
                    )}
                  </Field>
                ) : (
                  <Field name="txnRefId">
                    {(p: FieldProps) => (
                      <FloatingInputField
                        {...p}
                        label="Transaction Reference ID"
                        maxLength={50}
                        required
                      />
                    )}
                  </Field>
                )}

                {/* Reason */}
                <UniversalSelect
                  id="disposition"
                  label="Reason for raising the concern"
                  placeholder={
                    dispositionsBox.loading ? "Loading..." : "-- choose --"
                  }
                  items={dispositionItems}
                  multiple={false}
                  value={fp.values.disposition}
                  loading={dispositionsBox.loading}
                  disabled={
                    submitting || fp.isSubmitting || dispositionsBox.loading
                  }
                  onChange={(val) => {
                    fp.setFieldTouched("disposition", true, false);
                    fp.setFieldValue("disposition", String(val));
                  }}
                  error={
                    (fp.touched.disposition && fp.errors.disposition
                      ? String(fp.errors.disposition)
                      : undefined) ||
                    dispositionsBox.error ||
                    undefined
                  }
                />

                {/* Description */}
                <Field name="description">
                  {(p: FieldProps) => {
                    const descValue = String(p.field.value ?? "");
                    return (
                      <Box>
                        <FloatingTextareaField
                          {...p}
                          label="Description"
                          maxLength={500}
                          required
                        />
                        <HStack justify="space-between" mt={1}>
                          <Box />
                          <Text fontSize="xs" color="gray.500">
                            {descValue.length}/500
                          </Text>
                        </HStack>
                      </Box>
                    );
                  }}
                </Field>

                <Button
                  type="submit"
                  h="48px"
                  rounded="lg"
                  loading={submitting || fp.isSubmitting}
                  disabled={!canSubmit}
                >
                  Register Complaint
                </Button>

                {/* Error */}
                {error && (
                  <Box
                    p={3}
                    rounded="lg"
                    bg="red.50"
                    borderWidth="1px"
                    borderColor="red.100"
                  >
                    <Text fontWeight="semibold" color="red.700">
                      {error}
                    </Text>
                  </Box>
                )}

                {/* Success Result */}
                {result && (
                  <Box
                    p={4}
                    rounded="xl"
                    bg="green.50"
                    borderWidth="1px"
                    borderColor="green.100"
                  >
                    <Text fontWeight="bold" color="green.800">
                      Complaint Registered Successfully
                    </Text>

                    <VStack align="stretch" mt={3} gap={2}>
                      <Row label="Ticket ID" value={result.ticketId} />
                      <Row label="Ticket Status" value={result.ticketStatus} />
                      <Row label="Reason" value={result.responseReason} />
                      <Row label="Ticket Type" value={result.ticketType} />
                      {result.assigned && (
                        <Row label="Assigned" value={result.assigned} />
                      )}
                    </VStack>
                  </Box>
                )}
              </VStack>
            </Form>
          );
        }}
      </Formik>
    </BbpsComplainLayout>
  );
};

export default RaiseTxnComplaintPage;
